import {
  ChangeDetectionStrategy,
  Component,
  computed,
  Inject,
  inject,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from "@angular/core";
import { toSignal, toObservable } from "@angular/core/rxjs-interop";
import { FormControl, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";

import { merge, Subscription } from "rxjs";
import { debounceTime, map } from "rxjs/operators";

import { DeliveryOverlayService } from "@components/deliveries/pages/delivery-overlay/delivery-overlay.service";
import { DeliveryReportService } from "@components/reports/delivery-report/delivery-report.service";
import { ConfirmDialogComponent } from "@components/shared/confirm-dialog/confirm-dialog.component";
import { ItemSupplyChainMapperService } from "@components/shared/items-supply-chain/item-supply-chain-mapper.service";
import { AttachmentsCommonService } from "@components/shared/overlay/attachments-common.service";
import { SlideOverlayPageService } from "@components/shared/overlay/slide-overlay-page/slide-overlay-page.service";
import { TextConstants } from "@shared/constants";
import {
  AttachmentTargetEnum,
  AttachmentTypeEnum,
  ConfirmDialogResponseEnum,
  DeliveryStatusEnum,
  FeatureFlagEnum,
  RouteEnum,
} from "@shared/enums";
import {
  IConfig,
  IConnectionExtended,
  IDelivery,
  IDeliveryDetails,
  IDeliveryExtended,
  IDocument,
  IDocumentType,
  IItem,
  IItemSupplyChain,
  ILocationDetails,
  ILocationExtended,
  IMaterial,
  IProcessType,
  IProductExtended,
} from "@shared/interfaces";
import {
  NotificationService,
  AuthenticationService,
  ConnectionsService,
  DeliveriesService,
  DocumentTypesService,
  ItemsService,
  LocationsService,
  ProcessTypesService,
  FeatureFlagService,
  LocalizationService,
  CommonService,
} from "@shared/services";
import { ApiService } from "@shared/services/api/api.service";
import { GraphService } from "@shared/services/api/graph.service";
import { RouterService } from "@shared/services/router.service";
import { APP_CONFIG } from "@shared/tokens";
import { CommonUtils } from "@shared/utils";

interface IPrintSectionFormGroup {
  includeMainLocationsInfo: FormControl<boolean>;
  includeTraderInfo: FormControl<boolean>;
  includeSupplyChain: FormControl<boolean>;
  includeProductInfo: FormControl<boolean>;
  includeAdditionalInfo: FormControl<boolean>;
  includeProductTransformations: FormControl<boolean>;
  includeSupplyChainEntities: FormControl<boolean>;
  includeLocationInfo: FormControl<boolean>;
  includeLocationDetails: FormControl<boolean>;
  includeOrganisationCertificatesDocuments: FormControl<boolean>;
  includeOtherDeliveries: FormControl<boolean>;
}

export type IDeliveryReportPrintSectionFormGroupValue = {
  [K in keyof IPrintSectionFormGroup]: boolean;
};

@Component({
  standalone: false,
  selector: "app-delivery-report",
  templateUrl: "./delivery-report.component.html",
  styleUrls: ["./delivery-report.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryReportComponent implements OnInit, OnDestroy {
  protected readonly route: ActivatedRoute = inject(ActivatedRoute);

  private readonly notificationService: NotificationService = inject(NotificationService);

  private readonly deliveriesService: DeliveriesService = inject(DeliveriesService);

  private readonly locationsService: LocationsService = inject(LocationsService);

  private readonly itemsService: ItemsService = inject(ItemsService);

  private readonly connectionsService: ConnectionsService = inject(ConnectionsService);

  private readonly documentTypesService: DocumentTypesService = inject(DocumentTypesService);

  protected readonly routerService: RouterService = inject(RouterService);

  private readonly router: Router = inject(Router);

  private readonly itemSupplyChainMapperService: ItemSupplyChainMapperService = inject(
    ItemSupplyChainMapperService,
  );

  private readonly graphService: GraphService = inject(GraphService);

  private readonly authenticationService: AuthenticationService = inject(AuthenticationService);

  private readonly deliveryOverlayService: DeliveryOverlayService = inject(DeliveryOverlayService);

  private readonly slideOverlayPageService: SlideOverlayPageService =
    inject(SlideOverlayPageService);

  private readonly processTypesService: ProcessTypesService = inject(ProcessTypesService);

  private readonly deliveryReportService: DeliveryReportService = inject(DeliveryReportService);

  private readonly dialog: MatDialog = inject(MatDialog);

  private readonly apiService: ApiService = inject(ApiService);

  private readonly featureFlagService: FeatureFlagService = inject(FeatureFlagService);

  private readonly commonService = inject(CommonService);

  private readonly attachmentsCommonService: AttachmentsCommonService =
    inject(AttachmentsCommonService);

  private readonly localizationService: LocalizationService = inject(LocalizationService);

  public delivery: IDelivery;

  public customDeliveryFieldsValues: { label: string; value: string }[] = [];

  public sender: ILocationExtended;

  public receiver: ILocationExtended;

  public allProcessTypes: IProcessType[] = [];

  public traders: IConnectionExtended[] = [];

  public documentTypes: IDocumentType[] = [];

  public isLoading = signal<boolean>(true);

  public isPrintView = signal<boolean>(false);

  public isLoadingSupplyChainInfo = signal<boolean>(true);

  public supplyChainItems: IItemSupplyChain[];

  public documents: IDocument[] = [];

  public supplyChainLocations: ILocationExtended[] = [];

  public supplyChainDeliveries: IDeliveryExtended[] = [];

  public items: IItem[] = [];

  public deliveryItemIds: string[] = [];

  public deliveryDetails: IDeliveryDetails;

  protected readonly subscriptions = new Subscription();

  public readonly additionalInformationText = TextConstants.ADDITIONAL_INFORMATION;

  private readonly activeOrganisationId = this.authenticationService.getActiveOrganisationId();

  public readonly isRegularUser = this.authenticationService.isRegularUser();

  private readonly isSharedUser = this.authenticationService.isSharedUser();

  public readonly mainInformationText = TextConstants.MAIN_INFORMATION;

  public readonly isDeliveryReportPrintEnabled = this.featureFlagService.isEnabled(
    FeatureFlagEnum.DELIVERY_REPORT_PRINT,
  );

  public readonly printSectionId = "delivery-report-print-section";

  public readonly supplyChainHeightFn = CommonUtils.getOverlaySupplyChainHeight;

  private readonly printOptionsTemplate = viewChild("printOptionsTemplate", { read: TemplateRef });

  public isRegularMode = computed(() => !this.isPrintView());

  private readonly requestActivity$ = merge(
    this.graphService.lastRequestTimestamp$,
    this.apiService.lastRequestTimestamp$,
    toObservable(this.isLoadingSupplyChainInfo),
  ).pipe(
    map(() => true),
    debounceTime(3000),
    map(() => false),
  );

  private readonly requestActivitySignal = toSignal(this.requestActivity$, { initialValue: true });

  public readonly isReadyToPrint = computed(() => !this.requestActivitySignal());

  public readonly allUnitOfMeasurements = computed(() =>
    this.commonService.systemUnitsOfMeasurements(),
  );

  public printSectionFormGroupValue: IDeliveryReportPrintSectionFormGroupValue;

  public readonly translations: any = {
    title: $localize`:@@delivery-report.deliveryreport:Delivery report`,
    mainLocationsInfo: $localize`:@@delivery-report.mainlocationsinfo:Main locations information`,
    traderInfo: $localize`:@@delivery-report.traderinfo:Trader information`,
    productInfo: $localize`:@@delivery-report.productinfo:Product information`,
    additionalInfo: $localize`:@@delivery-report.additionalinfo:Additional information`,
    productTransformations: $localize`:@@delivery-report.producttransformations:Product transformations`,
    supplyChainEntities: $localize`:@@delivery-report.supplychainentities:Supply chain entities`,
    locationInfo: $localize`:@@delivery-report.locationinfo:Location information`,
    locationDetails: $localize`:@@delivery-report.locationdetails:Location details`,
    organisationCertificatesDocuments: $localize`:@@delivery-report.certsanddocs:Organisation certificates & documents`,
    otherDeliveries: $localize`:@@delivery-report.otherdeliveries:Other deliveries`,
  };

  public printSectionFormGroup: FormGroup<IPrintSectionFormGroup>;

  printStyle: any = {
    body: { overflow: "visible !important" },
    "@page": {
      size: "A4",
      margin: "10mm",
    },
    "@media print": {
      "body, html": {
        height: "auto !important",
        overflow: "visible !important",
      },
      [`#${this.printSectionId}`]: {
        overflow: "visible !important",
        height: "auto !important",
        "page-break-after": "always",
      },
    },
  };

  public onNavigateToPrintable(): void {
    this.printSectionFormGroup = this.buildFormGroupSections();

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: "reduced-padding",
      data: {
        title: $localize`:@@title.printreport:Print report`,
        contentText: $localize`:@@confirm.selectreportsections:Select which report sections you want to include in printing`,
        confirmButtonText: $localize`:@@shared.print:Print`,
        contentTemplate: this.printOptionsTemplate(),
        formGroup: this.printSectionFormGroup,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === ConfirmDialogResponseEnum.CONFIRM) {
        const queryParams = new URLSearchParams();

        Object.entries(this.printSectionFormGroup.value).forEach(([key, value]) => {
          if (value) {
            queryParams.append(key, value.toString());
          }
        });

        const url = `${RouteEnum.DELIVERY_REPORT_PRINT_VIEW}/${this.delivery.id}?${queryParams.toString()}`;

        CommonUtils.openInNewTab(url);
      }
    });
  }

  constructor(@Inject(APP_CONFIG) private environment: IConfig) {
    CommonUtils.loadGoogleMapsScripts(
      this.environment.googleMapsApiKey,
      this.localizationService.getLanguage(),
    );

    this.subscriptions.add(
      this.slideOverlayPageService.trigger$.subscribe(async (params) => {
        if (params?.hasSaved) {
          this.isLoading.set(true);
          const deliveryId = this.route.snapshot.params["id"];

          const elementLoaded = await this.loadData(deliveryId);

          if (!elementLoaded) {
            return;
          }
          this.isLoading.set(false);
        }
      }),
    );
  }

  public async ngOnInit(): Promise<void> {
    const deliveryId = this.route.snapshot.params["id"];

    if (!deliveryId) {
      await this.handleDeliveryNotFoundError();

      return;
    }

    this.isPrintView.set(this.route.snapshot.url.some((segment) => segment.path === "print-view"));

    try {
      const elementLoaded = await this.loadData(deliveryId);

      if (!elementLoaded) {
        return;
      }
      this.isLoading.set(false);
      this.loadAdditionalData();
    } catch (error) {
      this.notificationService.showError(error);
    }

    if (!this.isPrintView()) {
      this.printSectionFormGroup = this.buildFormGroupSections();
      this.printSectionFormGroupValue = this.printSectionFormGroup
        .value as IDeliveryReportPrintSectionFormGroupValue;

      return;
    }

    this.printSectionFormGroup = this.buildFormGroupSections(false);

    const queryParams = this.routerService.getQueryParams();

    Object.entries(queryParams).forEach(([key, value]) => {
      this.printSectionFormGroup.patchValue({
        [key]: value === "true",
      });
    });

    this.printSectionFormGroupValue = this.printSectionFormGroup
      .value as IDeliveryReportPrintSectionFormGroupValue;

    this.subscriptions.add(
      this.graphService.lastRequestTimestamp$.subscribe((timestamp) => {
        console.log("Last request timestamp:", timestamp);
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected buildFormGroupSections(defaultInclusionValue: boolean = true): FormGroup {
    const form = new FormGroup(
      {
        includeMainLocationsInfo: new FormControl(defaultInclusionValue),
        includeTraderInfo: new FormControl(defaultInclusionValue),
        includeProductInfo: new FormControl(defaultInclusionValue),
        includeAdditionalInfo: new FormControl(defaultInclusionValue),
        includeProductTransformations: new FormControl(defaultInclusionValue),
        includeSupplyChainEntities: new FormControl(defaultInclusionValue),
        includeLocationInfo: new FormControl(defaultInclusionValue),
        includeLocationDetails: new FormControl(defaultInclusionValue),
        includeOrganisationCertificatesDocuments: new FormControl(defaultInclusionValue),
        includeOtherDeliveries: new FormControl(defaultInclusionValue),
      },
      { validators: this.atLeastOneCheckboxSelected },
    );

    return form;
  }

  private atLeastOneCheckboxSelected(group: FormGroup): { error: true } {
    const values = Object.values(group.value);
    const hasChecked = values.some((value) => value === true);

    return hasChecked ? null : { error: true };
  }

  public get allProducts(): IProductExtended[] {
    return this.deliveryReportService.allProducts;
  }

  public get allMaterials(): IMaterial[] {
    return this.deliveryReportService.allMaterials;
  }

  public statusDisplayText = computed(() => {
    const mappings = {
      [DeliveryStatusEnum.DELIVERED]: $localize`:@@delivery-overlay.status.delivered:Delivered`,
      [DeliveryStatusEnum.PENDING]: $localize`:@@delivery-overlay.status.pending:Pending`,
      [DeliveryStatusEnum.IN_PROGRESS]: $localize`:@@delivery-overlay.status.inprogress:In progress`,
    };

    return mappings[this.delivery.status] || "-";
  });

  private async loadData(deliveryId: string): Promise<boolean> {
    try {
      this.delivery = await this.deliveriesService.get(deliveryId);
    } catch {
      await this.handleDeliveryNotFoundError();

      return false;
    }

    await Promise.all([
      this.loadCustomFields(),
      this.loadTraders(),
      this.loadLocations(),
      this.loadProducts(),
      this.loadMaterials(),
      this.loadItems(),
      this.loadDocumentTypes(),
    ]);

    return true;
  }

  private async loadAdditionalData(): Promise<void> {
    this.loadSupplyChainInfo();
  }

  private async loadSupplyChainInfo(): Promise<void> {
    await Promise.all([this.loadProcessTypes(), this.setDeliveryDetailsForMap(this.delivery)]);

    this.deliveryItemIds = this.delivery.items.map((item) => CommonUtils.getUriId(item.item));
    this.supplyChainItems = await this.itemSupplyChainMapperService.mapItemSupplyChain(
      this.deliveryItemIds,
    );

    this.supplyChainLocations = this.supplyChainItems
      .flatMap((item) => item.locations)
      .filter((location, index, self) => index === self.findIndex((l) => l.id === location.id));

    const supplyChainDeliveryIds = this.supplyChainItems
      .flatMap((item) => item.deliveries)
      .filter(
        (delivery, index, self) =>
          delivery.id !== this.delivery.id && index === self.findIndex((d) => d.id === delivery.id),
      )
      .map((delivery) => delivery.id);

    this.supplyChainDeliveries = await this.deliveriesService.getByIdsGraphQL(
      supplyChainDeliveryIds,
      undefined,
      ["ITEMS", "CERTIFICATES_WITH_DOCUMENTS", "DOCUMENTS", "CUSTOM_FIELDS"],
    );

    this.isLoadingSupplyChainInfo.set(false);
  }

  private async loadCustomFields(): Promise<void> {
    await this.deliveryReportService.loadAllCustomDeliveryFields();

    this.customDeliveryFieldsValues = this.deliveryReportService.getCustomFieldValuesForDelivery(
      this.delivery,
    );
  }

  private async loadTraders(): Promise<void> {
    const traderIds = this.delivery.agents.map((agentUri) => CommonUtils.getUriId(agentUri));

    this.traders = await this.connectionsService.getByIdsGraphQL(traderIds, undefined, [
      "FULL_ADDRESS",
      "CERTIFICATES",
      "CUSTOM_FIELDS",
    ]);
  }

  private async loadLocations(): Promise<void> {
    const fromLocationId = CommonUtils.getUriId(this.delivery.from);
    const toLocationId = CommonUtils.getUriId(this.delivery.to);

    const locations = await this.locationsService.getByIdsGraphQL(
      [fromLocationId, toLocationId],
      undefined,
      ["CERTIFICATES", "CUSTOM_FIELDS", "FULL_ADDRESS"],
    );

    this.sender = locations[0].id === fromLocationId ? locations[0] : locations[1];
    this.receiver = locations[0].id === fromLocationId ? locations[1] : locations[0];
  }

  private async loadProducts(): Promise<void> {
    await this.deliveryReportService.loadProducts();
  }

  private async loadDocumentTypes(): Promise<void> {
    const [deliveryDocuments, allDocumentTypes] = await Promise.all([
      this.attachmentsCommonService.loadSelectedAttachments(
        AttachmentTypeEnum.DOCUMENT,
        AttachmentTargetEnum.DELIVERY,
        this.delivery.id,
      ) as Promise<IDocument[]>,
      this.documentTypesService.getAll(),
    ]);

    const missingDocumentTypes = (
      await this.deliveryOverlayService.allMissingDocumentTypesByRuleSet(
        AttachmentTargetEnum.DELIVERY,
        this.delivery.id,
        allDocumentTypes,
      )
    ).map((missingDocumentType) => {
      return {
        name: missingDocumentType.types,
        id: null,
        createdTime: null,
        rulesetsNames: [missingDocumentType.ruleset.name],
      };
    });

    const deliveryDocumentTypes: IDocumentType[] = [];

    for (const documentType of allDocumentTypes) {
      const documents = deliveryDocuments.filter((document) => document.type === documentType.name);

      if (!documents.length) {
        continue;
      }

      deliveryDocumentTypes.push({ ...documentType, documents });
    }

    this.documentTypes = [...deliveryDocumentTypes, ...missingDocumentTypes];
  }

  private async loadMaterials(): Promise<void> {
    this.deliveryReportService.loadMaterials();
  }

  private async loadItems(): Promise<void> {
    const itemIds = this.delivery.items.map((item) => CommonUtils.getUriId(item.item));

    const itemsExtended = await this.itemsService.getByIdsGraphQL(itemIds);

    this.items = itemsExtended.map((item) => {
      const deliveredQuantity = this.delivery.items.find(
        (deliveryItem) => CommonUtils.getUriId(deliveryItem.item) === item.id,
      )?.quantity;

      return CommonUtils.convertExtendedItemToItem(
        { ...item, deliveredQuantity },
        this.activeOrganisationId,
        this.isSharedUser,
      );
    });
  }

  private async setDeliveryDetailsForMap(element: IDelivery): Promise<void> {
    let from: ILocationDetails;
    let to: ILocationDetails;
    const fromId = CommonUtils.getUriId(element.from);
    const toId = CommonUtils.getUriId(element.to);

    try {
      const [fromLocation, toLocation] = await Promise.all([
        this.locationsService.get(fromId),
        this.locationsService.get(toId),
      ]);

      from = fromLocation;
      to = toLocation;
    } catch (error) {
      if (this.isRegularUser) {
        this.notificationService.showError(error);
      }
    }

    this.deliveryDetails = { ...element, from, to };
  }

  private async loadProcessTypes(): Promise<void> {
    this.allProcessTypes = await this.processTypesService.getAll();
  }

  private async handleDeliveryNotFoundError(): Promise<void> {
    this.notificationService.showError($localize`:@@shared.deliverynotfound:Delivery not found`);
    if (this.routerService.hasHistory()) {
      this.routerService.goBackInHistory();
    } else {
      await this.router.navigate([`/${RouteEnum.DELIVERIES}`]);
    }
  }
}
